import React, { useCallback } from 'react';
import { makeStyles, Theme } from '@material-ui/core/styles';
import AppBar from '@material-ui/core/AppBar';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Box from '@material-ui/core/Box';
import {
    DataGrid,
    GridEditCellValueParams,
    GridFilterModel,
    GridFilterModelParams,
    GridOverlay,
    GridPageChangeParams,
    GridRowData,
    GridRowId,
    GridRowParams,
    GridRowSelectedParams,
    GridSelectionModelChangeParams,
    GridSortModel,
    GridSortModelParams,
} from '@material-ui/data-grid';
import { ConfirmedCourseGridColumns, BannerCourseGridColumns, getCourseGridRowID } from './CourseGridColumns';
import { CoursesBannerCustomGridToolbar } from './CoursesBannerCustomGridToolbar';
import { CourseBannerCustomGridFooter } from './CourseBannerCustomGridFooter';
import { CoursesConfirmedCustomGridToolbar } from './CoursesConfirmedCustomGridToolbar';
import { LinearProgress } from '@material-ui/core';
import { CourseConfirmedCustomGridFooter } from './CourseConfirmedCustomGridFooter';

import { dataGridRowsPerPage } from '../../constants';
import { ServiceRequestsDataGridColDef } from '../ServiceRequest/ServiceRequestGridColDef';
import { ServiceRequest } from '../../redux/serviceRequest/serviceRequestTypes';
import { ServiceRequestCustomGridToolbar } from '../ServiceRequest/ServiceRequestCustomGridToolbar';
import { ServiceRequestCustomGridFooter } from '../ServiceRequest/ServiceRequestCustomGridFooter';
import { ServiceRequestsDataGridState, DataGridState } from '../../redux/application/applicationInterfaces';
import { ICourse } from '../../redux/course/courseInterfaces';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

/**
 * Returns a TabPanel component containing passed child components
 *
 * @param props
 * @returns A TabPanel
 */
const TabPanel = (props: TabPanelProps) => {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`courses-tabpanel-${index}`}
            aria-labelledby={`courses-tab-${index}`}
            {...other}
        >
            {value === index && <Box p={3}>{children}</Box>}
        </div>
    );
};

function a11yProps(index: number) {
    return {
        id: `courses-tab-${index}`,
        'aria-controls': `courses-tabpanel-${index}`,
    };
}

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        flexGrow: 1,
        backgroundColor: theme.palette.background.paper,
        '& .lc-courses--bannerSync': {
            color: theme.palette.info.main,
            fontWeight: 'bold',
        },
        '& .lc-courses--bannerSync .MuiTypography-subtitle2': {
            color: theme.palette.info.main,
            fontWeight: 'bold',
        },
        '& .lc-courses--bannerSync:hover': {},
    },
    linearProgress: {
        height: '6px',
        backgroundColor: '#eee',
        '& .MuiLinearProgress-barColorSecondary': {
            backgroundColor: theme.palette.info.light,
        },
    },
    dataGrid: {
        '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within': {
            outline: 'none',
        },
    },
    filterPanel: {
        marginTop: '-4.5rem',
    },
}));

interface ICourseTabViewProps {
    courses: ICourse[];
    bannerCourses: ICourse[];
    confirmedCourses: ICourse[];
    isLoadingCourses: boolean;
    serviceRequests: ServiceRequest[];
    serviceRequestsDataGridState: ServiceRequestsDataGridState;
    isLoadingServiceRequests: boolean;
    bannerSyncCandidates: (string | undefined)[];
    activeCourseTab: number;
    dataGridState: DataGridState;
    confirmedCourseSelectionModel: string[];
    handleTabChange: (tabIndex: number) => void;
    handleCourseConfirm: (props: Map<GridRowId, GridRowData>) => void;
    handleCoursesDelete: (e: React.ChangeEvent<{ value: unknown }>) => void;
    handleCourseConfirmedSelect: (param: GridRowSelectedParams) => void;
    handleConfirmedCourseSelectionModelChange: (param: GridSelectionModelChangeParams) => void;

    handleServiceRequestConfirm: (props: Map<GridRowId, ServiceRequest>) => void;
    handleServiceRequestCancel: (props: Map<GridRowId, ServiceRequest>) => void;
    handleServiceRequestDataGridPageChange: (params: GridPageChangeParams) => void;
    handleServiceRequestDataGridFilterChange: (model: GridFilterModel) => void;
    handleServiceRequestDataGridSortChange: (model: GridSortModel) => void;
    handleServiceRequestDataGridPerPageChange: (params: GridPageChangeParams) => void;

    handleBannerCourseCellValueChange: (param: GridEditCellValueParams) => void;
    handleDataGridPageChange: (params: GridPageChangeParams) => void;
    handleDataGridFilterChange: (model: GridFilterModel) => void;
}

/**
 * Renders a tabbed view of courses
 *
 * @param props
 * @returns A tabbed view component
 */
const CourseTabView: React.FC<ICourseTabViewProps> = (props) => {
    const classes = useStyles();

    const handleTabChange = (event: React.ChangeEvent<Record<string, never>>, newValue: number) => {
        props.handleTabChange(newValue);
    };

    const handleDataGridPageChange = (params: GridPageChangeParams) => {
        props.handleDataGridPageChange(params);
    };

    const handleFilterModelChange = (model: GridFilterModelParams) => {
        props.handleDataGridFilterChange(model.filterModel);
    };

    const handleServiceRequestsFilterModelChange = (params: GridFilterModelParams) => {
        props.handleServiceRequestDataGridFilterChange(params.filterModel);
    };

    const handleServiceRequestsSortModelChange = (params: GridSortModelParams) => {
        props.handleServiceRequestDataGridSortChange(params.sortModel);
    };

    const getRowClassName = useCallback(
        (params: GridRowParams) => {
            const courseID = params.row.courseID;
            const className = props.bannerSyncCandidates.includes(courseID) ? 'bannerSync' : 'row';
            return `lc-courses--${className}`;
        },
        [props.bannerSyncCandidates],
    );

    const CustomLoadingOverlay = () => {
        return (
            <GridOverlay style={{top:0}}>
                <div style={{ position: 'relative', top: "-1.5rem", width: '100%', zIndex: 99 }}>
                    <LinearProgress color="secondary" className={classes.linearProgress} />
                </div>
            </GridOverlay>
        );
    };

    const tabLabels: string[] = ['Confirmed', 'Review', 'Banner'];

    return (
        <div className={classes.root}>
            <AppBar position="static">
                <Tabs value={props.activeCourseTab} onChange={handleTabChange} aria-label="course tabs">
                    {tabLabels.map((label, index) => {
                        return <Tab label={label} {...a11yProps(index)} key={index} />;
                    })}
                </Tabs>
            </AppBar>
            <TabPanel value={props.activeCourseTab} index={0}>
                {props.activeCourseTab === 0 && (
                    <DataGrid
                        getRowId={getCourseGridRowID}
                        rows={props.confirmedCourses}
                        getRowClassName={getRowClassName}
                        columns={ConfirmedCourseGridColumns}
                        pageSize={props.dataGridState.perPage}
                        page={props.dataGridState.page}
                        onPageChange={handleDataGridPageChange}
                        onPageSizeChange={handleDataGridPageChange}
                        rowsPerPageOptions={dataGridRowsPerPage}
                        pagination
                        className={classes.dataGrid}
                        loading={props.isLoadingCourses}
                        autoHeight={true}
                        checkboxSelection
                        disableSelectionOnClick={true}
                        components={{
                            Toolbar: CoursesConfirmedCustomGridToolbar,
                            LoadingOverlay: CustomLoadingOverlay,
                            Footer: CourseConfirmedCustomGridFooter,
                        }}
                        componentsProps={{
                            toolbar: {
                                handleCourseDelete: props.handleCoursesDelete,
                                selectedCourses: props.confirmedCourseSelectionModel,
                            },
                            footer: {
                                handleCourseDelete: props.handleCoursesDelete,
                                selectedCourses: props.confirmedCourseSelectionModel,
                            },
                            panel: {
                                className: classes.filterPanel,
                            },
                        }}
                        onRowSelected={props.handleCourseConfirmedSelect}
                        selectionModel={props.confirmedCourseSelectionModel}
                        onSelectionModelChange={props.handleConfirmedCourseSelectionModelChange}
                        onFilterModelChange={handleFilterModelChange}
                        filterModel={props.dataGridState.filterModel}
                    />
                )}
            </TabPanel>
            <TabPanel value={props.activeCourseTab} index={1}>
                {props.activeCourseTab === 1 && (
                    <DataGrid
                        autoHeight
                        pagination
                        pageSize={props.serviceRequestsDataGridState.perPage}
                        page={props.serviceRequestsDataGridState.page}
                        onPageChange={props.handleServiceRequestDataGridPageChange}
                        onPageSizeChange={props.handleServiceRequestDataGridPerPageChange}
                        checkboxSelection
                        disableSelectionOnClick={true}
                        rows={props.serviceRequests}
                        loading={props.isLoadingServiceRequests}
                        columns={ServiceRequestsDataGridColDef}
                        onFilterModelChange={handleServiceRequestsFilterModelChange}
                        onSortModelChange={handleServiceRequestsSortModelChange}
                        getRowId={(row: GridRowData) => {
                            return row.courseID;
                        }}
                        filterModel={props.serviceRequestsDataGridState.filterModel}
                        sortModel={props.serviceRequestsDataGridState.sortModel}
                        components={{
                            Toolbar: ServiceRequestCustomGridToolbar,
                            LoadingOverlay: CustomLoadingOverlay,
                            Footer: ServiceRequestCustomGridFooter,
                        }}
                        componentsProps={{
                            toolbar: {
                                handleServiceRequestConfirm: props.handleServiceRequestConfirm,
                                handleServiceRequestCancel: props.handleServiceRequestCancel,
                            },
                            footer: {
                                handleServiceRequestConfirm: props.handleServiceRequestConfirm,
                                handleServiceRequestCancel: props.handleServiceRequestCancel,
                            },
                            panel: {
                                className: classes.filterPanel,
                            },
                        }}
                    />
                )}
            </TabPanel>

            <TabPanel value={props.activeCourseTab} index={2}>
                {props.activeCourseTab === 2 && (
                    <DataGrid
                        getRowId={getCourseGridRowID}
                        rows={props.bannerCourses}
                        getRowClassName={getRowClassName}
                        columns={BannerCourseGridColumns}
                        pageSize={props.dataGridState.perPage}
                        page={props.dataGridState.page}
                        onPageChange={handleDataGridPageChange}
                        onPageSizeChange={handleDataGridPageChange}
                        rowsPerPageOptions={dataGridRowsPerPage}
                        pagination
                        className={classes.dataGrid}
                        loading={props.isLoadingCourses}
                        autoHeight={true}
                        checkboxSelection
                        disableSelectionOnClick={true}
                        components={{
                            Toolbar: CoursesBannerCustomGridToolbar,
                            LoadingOverlay: CustomLoadingOverlay,
                            Footer: CourseBannerCustomGridFooter,
                        }}
                        componentsProps={{
                            toolbar: {
                                handleConfirmation: props.handleCourseConfirm,
                            },
                            footer: {
                                handleConfirmation: props.handleCourseConfirm,
                            },
                            panel: {
                                className: classes.filterPanel,
                            },
                        }}
                        onCellValueChange={props.handleBannerCourseCellValueChange}
                        onFilterModelChange={handleFilterModelChange}
                        filterModel={props.dataGridState.filterModel}
                    />
                )}
            </TabPanel>
        </div>
    );
};

export default CourseTabView;
